import React, { useState } from 'react';
import computeTensionFromGrams from '../utility/calculate_tension';
import { kpsiToGPa, gpaToKpsi } from '../utility/convert_stress';
import computeGramsFromKpsi from '../utility/calculate_gram';
import computeStressForceFromStrain from '../utility/calculate_stress_gram';
import  { ROUNDING_VALUES } from '../constants/calculator_constants';

import NumberField from './number-field';

const FiberTensionCalculator: React.FC = () => {
  const [claddingDiameter, setCladdingDiameter] = useState<number | string>(125);
  const [gramsForce, setGramsForce] = useState<number | string>(863);
  const [stressKpsi, setStressKpsi] = useState<number | string>(100);
  const [stressGPa, setStressGPa] = useState<number | string>(0.689);
  const [strainPercent, setStrainPercent] = useState<number | string>(0.95);
  
  // Error states
  const [errors, setErrors] = useState({
    claddingDiameter: '',
    gramsForce: '',
    stressKpsi: '',
    stressGPa: '',
    strainPercent: ''
  });

  const validateInput = (value: string | number, fieldName: string, min: number = 0, max: number = Infinity): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    
    if (numValue < min) {
      return `${fieldName} must be greater than or equal to ${min}`;
    }
    
    if (numValue > max) {
      return `${fieldName} must be less than or equal to ${max}`;
    }
    
    return '';
  };

  const updateError = (field: string, error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const getCladdingDiameter = (): number => {
    const value = typeof claddingDiameter === 'number' ? claddingDiameter : parseFloat(claddingDiameter);
    return isNaN(value) ? 125 : value;
  };

  const setFieldsToZero = () => {
    setGramsForce(0);
    setStrainPercent(0);
    setStressKpsi(0);
    setStressGPa(0);
  }

    const handleCladdingDiameterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const error = validateInput(value, 'Cladding Diameter', 0, 10000000);
      updateError('claddingDiameter', error);
      
      const v = parseFloat(value);
      if (!isNaN(v) && error === '') {
        setCladdingDiameter(v);
        setFieldsToZero();
      }
      else{
        setCladdingDiameter(value);
        setFieldsToZero();
      }
    }

    const handleGramForceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const error = validateInput(value, 'Grams Force', 0, 10000000);
      updateError('gramsForce', error);
      
      const v = parseFloat(value);
      if (!isNaN(v) && error === '') {
        setGramsForce(v);
        const fibercladdingDiameter = getCladdingDiameter();
        setCladdingDiameter(fibercladdingDiameter);
        const { kpsi, gpa, strainPercent } = computeTensionFromGrams(v, fibercladdingDiameter);
        setStressKpsi(typeof kpsi === 'string' ? kpsi : Number(kpsi).toFixed(ROUNDING_VALUES.STRESS_KPSI));
        setStressGPa(typeof gpa === 'string' ? gpa : Number(gpa).toFixed(ROUNDING_VALUES.STRESS_GPA));
        setStrainPercent(typeof strainPercent === 'string' ? strainPercent : Number(strainPercent).toFixed(ROUNDING_VALUES.STRAIN_PERCENT));
      }
      else{
        setGramsForce(value);
        setStressKpsi(0);
        setStressGPa(0);
        setStrainPercent(0);
      }
    };

    const handleStressKpsiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Stress (kpsi)', 0, 10000000);
    updateError('stressKpsi', error);
    
    const v = parseFloat(value);
    setStressKpsi(value);
    if (!isNaN(v) && error === '') {
      const gpa = kpsiToGPa(v);
      setStressGPa(Number(gpa.toFixed(ROUNDING_VALUES.STRESS_GPA)));
      const fibercladdingDiameter = getCladdingDiameter();
      setCladdingDiameter(fibercladdingDiameter);
      const { force, strainPercent } = computeGramsFromKpsi(v, fibercladdingDiameter, 3);
      setGramsForce(Number(force.toFixed(ROUNDING_VALUES.TENSION)));
      setStrainPercent(Number(strainPercent.toFixed(ROUNDING_VALUES.STRAIN_PERCENT)));
    }
    else{
      setGramsForce(0);
      setStressGPa(0);
      setStrainPercent(0);
    }
  };

  const handleStressGpaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Stress (GPa)', 0, 10000000);
    updateError('stressGPa', error);
    
    const v = parseFloat(value);
    setStressGPa(value);
    if (!isNaN(v) && error === '') {
      const kpsi_ret = gpaToKpsi(v);
      const kpsi = Number(kpsi_ret.toFixed(ROUNDING_VALUES.STRESS_KPSI));
      setStressKpsi(kpsi);
      const fibercladdingDiameter = getCladdingDiameter();
      setCladdingDiameter(fibercladdingDiameter);
      const { force, strainPercent } = computeGramsFromKpsi(kpsi_ret, fibercladdingDiameter);
      setGramsForce(Number(force.toFixed(ROUNDING_VALUES.TENSION)));
      setStrainPercent(Number(strainPercent.toFixed(ROUNDING_VALUES.STRAIN_PERCENT)));
    }
    else{
      setGramsForce(0);
      setStressKpsi(0);
      setStrainPercent(0);
    }
  };

  const handleStrainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Strain (%)', 0, 10000000);
    updateError('strainPercent', error);
    
    const v = parseFloat(value);
    setStrainPercent(value);
    if (!isNaN(v) && error === '') {
      const fibercladdingDiameter = getCladdingDiameter();
      setCladdingDiameter(fibercladdingDiameter);
      const { kpsi, gpa, grams } = computeStressForceFromStrain(v, fibercladdingDiameter);
      setStressKpsi(Number(kpsi.toFixed(ROUNDING_VALUES.STRESS_KPSI)));
      setStressGPa(Number(gpa.toFixed(ROUNDING_VALUES.STRESS_GPA)));
      setGramsForce(Number(grams.toFixed(ROUNDING_VALUES.TENSION)));
    }
    else{
      setGramsForce(0);
      setStressKpsi(0);
      setStressGPa(0);
    }
  };


  return (
    <div className="flex flex-col md:flex-row gap-8 md:gap-10 px-4 py-8 sm:p-8 bg-[#f2f4f7] rounded-xl" aria-label="Fiber in tension calculator">
      <div className="w-full">
        <h3 className="mb-5 font-theSans text-[1.3125rem] leading-[120%] tracking-0 text-black">Fiber in tension conversion calculator</h3>
        <p className="text-[1rem] text-[#101114] leading-[150%]">This calculator can be used to convert between force, stress, and strain values. The calculator updates the field values after each <em>keystroke</em>.</p>
      </div>
      <div className="w-full">
        <form 
          onSubmit={(e) => e.preventDefault()}
          className="w-full"
        >
          <fieldset className="flex flex-col gap-6">
            <legend className="sr-only">Fiber in tension conversion calculator</legend>
            {/* Cladding Diameter */}
            <NumberField
              label="Cladding Diameter"
              value={claddingDiameter}
              onChange={handleCladdingDiameterChange}
              unit="µm"
              title="Enter the fiber cladding diameter in micrometers"
              errors={errors?.claddingDiameter}
            />

            {/* Gram Force*/}
            <NumberField
              label="Grams (force)"
              value={gramsForce}
              onChange={handleGramForceChange}
              unit="g"
              title="Enter a positive number for force in grams"
              errors={errors?.gramsForce}
            />

            {/* Stress (Kpsi) */}
            <NumberField
              label="Stress"
              value={stressKpsi}
              onChange={handleStressKpsiChange}
              unit="kpsi"
              title="Enter a positive number for stress in kilopounds per square inch"
              errors={errors?.stressKpsi}
            />

            {/* Stress (GPa) */}
            <NumberField
              label="Stress"
              value={stressGPa}
              onChange={handleStressGpaChange}
              unit="GPa"
              step="0.001"
              title="Enter stress in gigapascals, or this will be calculated"
              errors={errors?.stressGPa}
            />

            {/* Strain (%) */}
            <NumberField
              label="Strain"
              value={strainPercent}
              onChange={handleStrainChange}
              unit="%"
              step="0.01"
              title="Enter a positive number for strain as a percentage"
              errors={errors?.strainPercent}
            />
          </fieldset>
        </form>
      </div>
    </div>
  );
};

export default FiberTensionCalculator;